/**
 * Route parser (R2.1, Task 1) — pure path <-> Route mapping, no imports.
 * `/` is the notebook list; `/signin` the sign-in view (R4.9 §7);
 * `/notebook/{tag}` a notebook (tag percent-decoded); the three literal
 * sub-names address sub-views; anything else is `notfound`, carrying the tag
 * only for a malformed sub-path under a known tag. Consumed by the history
 * wiring and view surfaces.
 *
 * `/settings/admin` (R4.9 §7, Task 11) is the account page — a sub-page of
 * settings rather than a sibling of it, because that is the only way it is
 * reached and the only way back. Exact matches only; it is checked before the
 * `/notebook/...` split, which does not address it anyway.
 */

export type Route =
  | { kind: 'list' }
  | { kind: 'signin' }
  | { kind: 'settings' }
  | { kind: 'admin' }
  | { kind: 'notebook'; tag: string; sub?: 'browse' | 'graveyard' | 'debuglog' }
  | { kind: 'notfound'; path: string; tag?: string };

export function parseRoute(pathname: string): Route {
  if (pathname === '/') return { kind: 'list' };
  if (pathname === '/signin') return { kind: 'signin' };
  if (pathname === '/settings') return { kind: 'settings' };
  if (pathname === '/settings/admin') return { kind: 'admin' };
  const [, head, rawTag, sub, extra] = pathname.split('/');
  if (head === 'notebook' && rawTag && extra === undefined) {
    let tag: string;
    try {
      tag = decodeURIComponent(rawTag);
    } catch {
      return { kind: 'notfound', path: pathname };
    }
    if (sub === undefined) return { kind: 'notebook', tag };
    if (sub === 'browse' || sub === 'graveyard' || sub === 'debuglog') {
      return { kind: 'notebook', tag, sub };
    }
    return { kind: 'notfound', path: pathname, tag };
  }
  return { kind: 'notfound', path: pathname };
}

export function buildRoute(route: Route): string {
  switch (route.kind) {
    case 'list':
      return '/';
    case 'signin':
      return '/signin';
    case 'settings':
      return '/settings';
    case 'admin':
      return '/settings/admin';
    case 'notebook': {
      const base = `/notebook/${encodeURIComponent(route.tag)}`;
      return route.sub ? `${base}/${route.sub}` : base;
    }
    case 'notfound':
      return route.path;
  }
}
